import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Upload, Landmark, RefreshCw, Lock, Check, X as XIcon, EyeOff,
  Search, ArrowRight, Loader2, FileText, AlertTriangle, Plus,
  ChevronLeft, ShieldCheck, Sparkles, History, Wand2, Bot,
} from 'lucide-react';
import AIMatchSuggestion from '../components/bank-recon/AIMatchSuggestion';

/**
 * Bank Reconciliation Workspace (Feb 2026).
 *
 * Three views inside one page:
 *   1) Statements list — pick a bank account, upload CSV, browse past
 *      statements.
 *   2) Statement detail — KPI strip + 4 tabs (Matched · Suggested ·
 *      Unmatched · Ignored) + per-line actions (Approve / Reject /
 *      Ignore / Manual Match) + Lock button.
 *   3) Manual-match drawer — opens from any line, lists candidates
 *      (bank tx / payments) that the engine considered.
 */

const fmt = (v) =>
  Number(v ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

const STATUS_AR = {
  matched:   'مُطابَق',
  suggested: 'مُقتَرَح',
  unmatched: 'غير مُطابِق',
  ignored:   'مُتجاهَل',
};

const STATUS_TONE = {
  matched:   'bg-emerald-100 text-emerald-800 border-emerald-300',
  suggested: 'bg-amber-100 text-amber-800 border-amber-300',
  unmatched: 'bg-slate-100 text-slate-700 border-slate-300',
  ignored:   'bg-zinc-100 text-zinc-500 border-zinc-300 line-through',
};


// ── KPI tile ────────────────────────────────────────────────────────


const KPI = ({ label, value, tone = 'slate', subtitle, testid }) => {
  const tones = {
    slate:   'border-slate-300 bg-white text-slate-800',
    emerald: 'border-emerald-300 bg-emerald-50/60 text-emerald-900',
    amber:   'border-amber-300 bg-amber-50/60 text-amber-900',
    red:     'border-red-300 bg-red-50/60 text-red-900',
    indigo:  'border-indigo-300 bg-indigo-50/60 text-indigo-900',
  }[tone];
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border p-3 ${tones}`}
    >
      <div className="text-[10px] uppercase tracking-wide font-semibold opacity-80">{label}</div>
      <div className="text-xl font-bold mt-1 font-mono">{value}</div>
      {subtitle && <div className="text-[10px] mt-0.5 opacity-70">{subtitle}</div>}
    </div>
  );
};


// ── Statements list view ────────────────────────────────────────────


function StatementsList({ accounts, statements, onPick, onUpload, uploading, bankAccountId, setBankAccountId, healthFilter, setHealthFilter, healthCounts }) {
  const fileRef = useRef(null);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Landmark className="w-6 h-6 text-indigo-600" />
            مَركَز المُطابَقة البَنكية
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            استورد كَشف الحِساب وَدع المُحرِّك يَربُط كُل بَنْد بسَنَدٍ في النِظام تِلقائياً.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <select
            data-testid="bank-account-picker"
            value={bankAccountId || ''}
            onChange={(e) => setBankAccountId(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white min-w-[260px]"
          >
            <option value="">— اختَر الحساب البَنكي —</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.bank_name || a.name} — {a.account_number || a.iban || ''}
              </option>
            ))}
          </select>

          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx,.xlsm,.pdf,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/pdf"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUpload(f);
              e.target.value = '';
            }}
            data-testid="bank-recon-file-input"
          />
          <button
            onClick={() => {
              if (!bankAccountId) { toast.error('اختَر حساباً بَنكياً أوَّلاً'); return; }
              fileRef.current?.click();
            }}
            disabled={uploading}
            data-testid="bank-recon-upload-btn"
            className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-semibold px-3 py-2 transition"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            استيراد CSV
          </button>
        </div>
      </div>

      <div className="rounded-xl border bg-white">
        <div className="px-4 py-2.5 border-b text-sm font-semibold text-slate-700 flex items-center gap-2 flex-wrap">
          <History className="w-4 h-4 text-slate-500" />
          كُشوف سابِقة <span className="text-slate-400">({statements.length})</span>
          <div className="flex items-center gap-1 ms-auto">
            {['all', 'green', 'amber', 'red'].map((b) => {
              const active = healthFilter === b;
              const cnt = b === 'all'
                ? (healthCounts.green || 0) + (healthCounts.amber || 0) + (healthCounts.red || 0)
                : (healthCounts[b] || 0);
              const cls = active ? 'bg-indigo-600 text-white border-indigo-600'
                : b === 'green' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : b === 'amber' ? 'bg-amber-50 text-amber-700 border-amber-200'
                : b === 'red'   ? 'bg-red-50 text-red-700 border-red-200'
                : 'bg-white text-slate-700 border-slate-200';
              const lbl = b === 'all' ? 'الكُل' : b === 'green' ? '✓ صَحي' : b === 'amber' ? '⚠ مُتَوَسِّط' : '✗ ضَعيف';
              return (
                <button key={b} onClick={() => setHealthFilter(b)}
                        data-testid={`health-filter-${b}`}
                        className={`text-[11px] font-semibold border rounded-full px-2.5 py-1 ${cls}`}>
                  {lbl} <span className="opacity-70">({cnt})</span>
                </button>
              );
            })}
          </div>
        </div>
        {statements.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            <FileText className="w-10 h-10 mx-auto opacity-30 mb-2" />
            لا يوجَد كُشوف بَعد. ارفع أوَّل كَشف لِبَدء المُطابَقة.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-start px-4 py-2">الملَف</th>
                  <th className="text-start px-4 py-2">الفَترة</th>
                  <th className="text-end px-4 py-2">عَدَد البُنود</th>
                  <th className="text-end px-4 py-2">رَصيد الإقفال</th>
                  <th className="text-start px-4 py-2">الجَودَة</th>
                  <th className="text-start px-4 py-2">الحالة</th>
                  <th className="text-start px-4 py-2">تَم في</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {statements.map((s) => {
                  const band = s.health_band || 'red';
                  const bandCls = band === 'green' ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                    : band === 'amber' ? 'bg-amber-100 text-amber-800 border-amber-300'
                    : 'bg-red-100 text-red-800 border-red-300';
                  return (
                  <tr key={s.id} className="border-t hover:bg-slate-50/60">
                    <td className="px-4 py-2 font-mono text-xs">{s.filename || '—'}</td>
                    <td className="px-4 py-2 text-xs text-slate-600">
                      {s.period_start || '—'} → {s.period_end || '—'}
                    </td>
                    <td className="px-4 py-2 text-end font-mono">{s.lines_count ?? 0}</td>
                    <td className="px-4 py-2 text-end font-mono">{fmt(s.closing_balance)}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center gap-1 text-[11px] font-semibold border rounded-full px-2 py-0.5 ${bandCls}`}
                            data-testid={`health-${s.id}`}>
                        {band === 'green' ? '✓' : band === 'amber' ? '⚠' : '✗'} {Math.round((s.health_score || 0) * 100)}%
                      </span>
                      {s.gate_allowed === false && (
                        <span className="inline-flex items-center gap-1 ms-1 text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded-full px-1.5 py-0.5"
                              data-testid={`gate-blocked-${s.id}`}>
                          🚫 بَوّابَة
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {s.is_locked ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-700 bg-zinc-100 border border-zinc-300 px-2 py-0.5 rounded-full">
                          <Lock className="w-3 h-3" /> مَقفول
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-300 px-2 py-0.5 rounded-full">
                          {s.status === 'matched' ? 'مُطابَقة' : 'مُستَورد'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500">{(s.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                    <td className="px-4 py-2 text-end">
                      <button
                        onClick={() => onPick(s.id)}
                        data-testid={`open-statement-${s.id}`}
                        className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 text-sm font-semibold"
                      >
                        فَتح <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


// ── Statement detail view ───────────────────────────────────────────


function StatementDetail({ stmtId, onBack, accounts }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('suggested');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [manualLine, setManualLine] = useState(null);
  const [aiLine, setAiLine] = useState(null);
  const [showReparse, setShowReparse] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [showExplain, setShowExplain] = useState(null);
  const [templates, setTemplates] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data: d } = await axiosInstance.get(`/bank-recon/${stmtId}`);
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'تَعَذَّر تَحميل الكَشف');
    }
  }, [stmtId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axiosInstance.get('/bank-mapping-templates')
      .then(({ data: d }) => setTemplates(d.items || []))
      .catch(() => {});
  }, []);

  const autoMatch = async (force = false) => {
    setBusy(true);
    try {
      const { data: r } = await axiosInstance.post(
        `/bank-recon/${stmtId}/auto-match`, null,
        { params: force ? { force: true } : {} },
      );
      toast.success(`تَمَّت المُطابَقة — ${r.matched} تِلقائي، ${r.suggested} مُقتَرَح`);
      await load();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det && typeof det === 'object' && det.error === 'import_gate_blocked') {
        setShowExplain({ gated: true, ...det });
      } else {
        toast.error(typeof det === 'string' ? det : det?.hint || 'فَشلت المُطابَقة التِلقائية');
      }
    } finally {
      setBusy(false);
    }
  };

  const lineAction = async (line, action) => {
    try {
      await axiosInstance.post(`/bank-recon/lines/${line.id}/${action}`);
      toast.success(action === 'approve' ? 'تَمَّ الاعتِماد' :
                     action === 'reject'  ? 'تَمَّ الرَفض' :
                     action === 'ignore'  ? 'تَمَّ التَجاهُل' : 'تَم');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فَشَلَت العَملية');
    }
  };

  const lock = async () => {
    if (!window.confirm('تأكيد قَفل الكَشف؟ لا يُمكن التَعديل بَعد القَفل.')) return;
    try {
      await axiosInstance.post(`/bank-recon/${stmtId}/lock`);
      toast.success('تَم قَفل الكَشف');
      await load();
    } catch (e) {
      const det = e?.response?.data?.detail;
      const msg = typeof det === 'string' ? det : det?.hint || 'فَشَل القَفل';
      toast.error(msg);
    }
  };

  const filtered = useMemo(() => {
    const lines = data?.lines || [];
    return lines.filter((l) => {
      if (tab !== 'all' && (l.match_status || 'unmatched') !== tab) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (l.description || '').toLowerCase().includes(q) ||
             (l.reference || '').toLowerCase().includes(q) ||
             String(l.amount).includes(q);
    });
  }, [data, tab, search]);

  const acct = useMemo(
    () => accounts.find((a) => a.id === data?.statement?.bank_account_id),
    [accounts, data],
  );

  if (!data) {
    return (
      <div className="flex items-center justify-center p-10 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin me-2" /> جارٍ تَحميل الكَشف…
      </div>
    );
  }

  const { statement: stmt, kpis } = data;
  const locked = !!stmt.is_locked;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            data-testid="back-to-statements"
            className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
          >
            <ChevronLeft className="w-4 h-4" /> العَودة
          </button>
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2">
              {locked && <Lock className="w-4 h-4 text-zinc-500" />}
              {stmt.filename || 'كَشف بَنكي'}
            </h2>
            <p className="text-xs text-slate-500">
              {acct ? `${acct.bank_name || acct.name} · ${acct.account_number || acct.iban || ''}` : ''}
              {' · '}{stmt.period_start} → {stmt.period_end}
            </p>
            {stmt.template_key && (
              <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full px-2 py-0.5 font-semibold"
                      data-testid="template-badge">
                  📋 {stmt.template_key} · v{stmt.template_version || 1}
                </span>
                {stmt.detection_confidence != null && (
                  <span className="text-slate-500">دِقَّة الكَشف: {Math.round((stmt.detection_confidence || 0) * 100)}%</span>
                )}
                {stmt.re_parse_count > 0 && (
                  <span className="text-amber-700">أُعيد تَحليلها {stmt.re_parse_count}×</span>
                )}
                {stmt.checksum_sha256 && (
                  <span className="text-slate-400 font-mono">SHA: {stmt.checksum_sha256.slice(0, 10)}…</span>
                )}
              </div>
            )}
            {stmt.validation && (
              <div className="text-[11px] mt-1 flex items-center gap-2 flex-wrap" data-testid="validation-badge">
                {(() => {
                  const c = stmt.validation.confidence;
                  const tone = c >= 0.95 ? 'emerald' : c >= 0.8 ? 'amber' : 'red';
                  const cls = {
                    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
                    amber:   'bg-amber-50 text-amber-800 border-amber-200',
                    red:     'bg-red-50 text-red-800 border-red-200',
                  }[tone];
                  const icon = c >= 0.95 ? '✓' : c >= 0.8 ? '⚠' : '✗';
                  return (
                    <button onClick={() => setShowExplain({ gated: false })}
                            data-testid="explain-health-btn"
                            className={`inline-flex items-center gap-1 border rounded-full px-2 py-0.5 font-semibold hover:opacity-80 transition ${cls}`}>
                      {icon} جودَة التَّحليل {Math.round((c || 0) * 100)}% · اشرَح
                    </button>
                  );
                })()}
                {stmt.validation.counts?.error  > 0 && <span className="text-red-700">أخطاء: {stmt.validation.counts.error}</span>}
                {stmt.validation.counts?.warning > 0 && <span className="text-amber-700">تَحذيرات: {stmt.validation.counts.warning}</span>}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => autoMatch(false)}
            disabled={busy || locked}
            data-testid="auto-match-btn"
            className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-semibold px-3 py-2"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            مُطابَقة تِلقائية
          </button>
          <button
            onClick={() => setShowReparse(true)}
            disabled={locked}
            data-testid="reparse-btn"
            title="إعادة تَحليل الملف الأَصلي بِقالِب آخَر"
            className="inline-flex items-center gap-1.5 rounded-lg bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 text-sm font-semibold px-3 py-2 disabled:opacity-50"
          >
            <RefreshCw className="w-4 h-4" />
            إعادة تَحليل
          </button>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/bank-recon/${stmtId}/file`}
            target="_blank" rel="noreferrer"
            data-testid="download-original-btn"
            title="تَنزيل الملف الأَصلي"
            className="inline-flex items-center gap-1.5 rounded-lg bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 text-sm font-semibold px-3 py-2"
          >
            <FileText className="w-4 h-4" />
            الأَصلي
          </a>
          <button
            onClick={() => setShowLog(true)}
            data-testid="parse-log-btn"
            className="inline-flex items-center gap-1.5 rounded-lg bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 text-sm font-semibold px-3 py-2"
          >
            <History className="w-4 h-4" />
            سِجِل التَحليل
          </button>
          <button
            onClick={lock}
            disabled={locked}
            data-testid="lock-statement-btn"
            className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-900 disabled:opacity-50 text-white text-sm font-semibold px-3 py-2"
          >
            <Lock className="w-4 h-4" />
            {locked ? 'مَقفول' : 'قَفل الفَترة'}
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
        <KPI testid="kpi-lines-total" label="إجمالي البُنود" value={kpis.lines_total} />
        <KPI testid="kpi-matched"     label="مُطابَق"        value={kpis.matched}   tone="emerald" />
        <KPI testid="kpi-suggested"   label="مُقتَرَح"        value={kpis.suggested} tone="amber" />
        <KPI testid="kpi-unmatched"   label="غير مُطابِق"     value={kpis.unmatched} tone="red" />
        <KPI testid="kpi-ignored"     label="مُتجاهَل"        value={kpis.ignored} />
        <KPI testid="kpi-match-rate"  label="نِسبة المُطابَقة" value={`${kpis.match_rate}%`} tone="indigo" />
        <KPI
          testid="kpi-statement-bal"
          label="رَصيد الكَشف"
          value={fmt(kpis.statement_balance)}
        />
        <KPI
          testid="kpi-difference"
          label="الفَرق"
          value={fmt(kpis.difference)}
          tone={Math.abs(kpis.difference || 0) < 0.01 ? 'emerald' : 'amber'}
          subtitle="كَشف − نِظام"
        />
      </div>

      {/* Tabs + Search */}
      <div className="rounded-xl border bg-white">
        <div className="flex items-center justify-between gap-2 px-3 pt-2 flex-wrap">
          <div className="flex items-center gap-1 flex-wrap">
            {['suggested', 'unmatched', 'matched', 'ignored'].map((k) => {
              const n = kpis[k] ?? 0;
              const active = tab === k;
              return (
                <button
                  key={k}
                  onClick={() => setTab(k)}
                  data-testid={`tab-${k}`}
                  className={`px-3 py-1.5 text-sm rounded-lg font-semibold border transition ${
                    active
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  {STATUS_AR[k]} <span className={`ms-1 text-xs ${active ? 'opacity-80' : 'opacity-50'}`}>({n})</span>
                </button>
              );
            })}
          </div>
          <div className="relative">
            <Search className="absolute start-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="recon-search"
              placeholder="بَحث في الوَصف / المَرجِع…"
              className="ps-7 pe-3 py-1.5 text-sm rounded-lg border border-slate-300 w-64"
            />
          </div>
        </div>

        {/* Lines table */}
        <div className="overflow-x-auto mt-2">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-start px-3 py-2">التاريخ</th>
                <th className="text-start px-3 py-2">الوَصف</th>
                <th className="text-start px-3 py-2">المَرجِع</th>
                <th className="text-end px-3 py-2">المَبلَغ</th>
                <th className="text-start px-3 py-2">الحالة</th>
                <th className="text-end px-3 py-2">الثِقة</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} className="p-8 text-center text-slate-400">لا توجَد بُنود في هذا التَبويب.</td></tr>
              ) : filtered.map((l) => {
                const st = l.match_status || 'unmatched';
                const top = (l.suggestions || [])[0];
                return (
                  <tr key={l.id} className="border-t hover:bg-slate-50/60 align-top">
                    <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">{l.date}</td>
                    <td className="px-3 py-2">
                      <div className="text-slate-800">{l.description || '—'}</div>
                      {(l.sadad_number || l.invoice_number) && (
                        <div className="text-[11px] text-slate-500 mt-0.5 flex gap-2">
                          {l.sadad_number && <span>SADAD: <span className="font-mono">{l.sadad_number}</span></span>}
                          {l.invoice_number && <span>INV: <span className="font-mono">{l.invoice_number}</span></span>}
                        </div>
                      )}
                      {l.rule_hit && (
                        <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full px-2 py-0.5"
                             data-testid={`rule-badge-${l.id}`}
                             title={`${l.rule_hit.pattern_type}: ${l.rule_hit.pattern}`}>
                          🛡️ {l.rule_hit.rule_name} · أَولَوِيَّة {l.rule_hit.priority}
                        </div>
                      )}
                      {top && top.scores && (
                        <div className="mt-1 flex flex-wrap gap-1" data-testid={`reasons-${l.id}`}>
                          {top.scores.filter(s => s.weight > 0 && s.passed).slice(0, 5).map((s, i) => (
                            <span key={i}
                                  className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5 font-mono">
                              ✓ {s.rule}{s.note ? `: ${s.note}` : ''}
                            </span>
                          ))}
                          {top.scores.filter(s => s.weight > 0 && !s.passed).slice(0, 2).map((s, i) => (
                            <span key={`x${i}`}
                                  className="text-[10px] bg-red-50 text-red-700 border border-red-200 rounded px-1.5 py-0.5 font-mono">
                              ✗ {s.rule}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{l.reference || '—'}</td>
                    <td className={`px-3 py-2 text-end font-mono ${l.amount < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                      {fmt(l.amount)}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-block text-[11px] font-semibold border rounded-full px-2 py-0.5 ${STATUS_TONE[st]}`}>
                        {STATUS_AR[st]}
                      </span>
                      {l.tier && st !== 'ignored' && (
                        <div className="text-[9px] mt-0.5 text-slate-500 uppercase">
                          {l.tier === 'auto' ? '🟢 Auto ≥95%' : l.tier === 'suggest' ? '🟡 Review 70-94%' : '⚪ Manual <70%'}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-end font-mono text-xs text-slate-700">
                      {l.confidence != null ? `${Math.round(l.confidence * 100)}%` : '—'}
                    </td>
                    <td className="px-3 py-2 text-end whitespace-nowrap">
                      {!locked && (
                        <div className="inline-flex items-center gap-1">
                          {st === 'suggested' && top && (
                            <button
                              onClick={() => lineAction(l, 'approve')}
                              data-testid={`line-approve-${l.id}`}
                              title="اعتِماد الاقتِراح"
                              className="p-1.5 rounded-md bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                          )}
                          {(st === 'suggested' || st === 'matched') && (
                            <button
                              onClick={() => lineAction(l, 'reject')}
                              data-testid={`line-reject-${l.id}`}
                              title="رَفض"
                              className="p-1.5 rounded-md bg-red-100 text-red-700 hover:bg-red-200"
                            >
                              <XIcon className="w-4 h-4" />
                            </button>
                          )}
                          {st !== 'ignored' && (
                            <button
                              onClick={() => lineAction(l, 'ignore')}
                              data-testid={`line-ignore-${l.id}`}
                              title="تَجاهُل"
                              className="p-1.5 rounded-md bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
                            >
                              <EyeOff className="w-4 h-4" />
                            </button>
                          )}
                          {st !== 'matched' && (
                            <button
                              onClick={() => setManualLine(l)}
                              data-testid={`line-manual-${l.id}`}
                              title="مُطابَقة يَدوية"
                              className="p-1.5 rounded-md bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                            >
                              <Sparkles className="w-4 h-4" />
                            </button>
                          )}
                          {l.confidence != null && l.confidence >= 0.50 && l.confidence < 0.70 && st !== 'matched' && (
                            <button
                              onClick={() => setAiLine(l)}
                              data-testid={`line-ai-${l.id}`}
                              title="استِشِر الذَّكاء الاصطِناعي"
                              className="p-1.5 rounded-md bg-gradient-to-l from-indigo-500 to-purple-500 text-white hover:from-indigo-600 hover:to-purple-600"
                            >
                              <Bot className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {manualLine && (
        <ManualMatchDrawer
          line={manualLine}
          onClose={() => setManualLine(null)}
          onMatched={async () => { setManualLine(null); await load(); }}
        />
      )}
      {aiLine && (
        <AIMatchSuggestion
          line={aiLine}
          candidates={aiLine.suggestions || []}
          onClose={() => setAiLine(null)}
          onAccepted={async () => { setAiLine(null); await load(); }}
        />
      )}

      {showReparse && (
        <ReparseModal
          stmt={stmt} templates={templates}
          onClose={() => setShowReparse(false)}
          onDone={async () => { setShowReparse(false); await load(); }}
        />
      )}

      {showLog && (
        <ParseLogModal stmtId={stmtId} onClose={() => setShowLog(false)} />
      )}

      {showExplain && (
        <ExplainHealthModal
          stmt={stmt} gated={showExplain.gated}
          gateInfo={showExplain.gated ? showExplain : null}
          onClose={() => setShowExplain(null)}
          onOpenReparse={() => { setShowExplain(null); setShowReparse(true); }}
          onOpenWizard={() => {
            window.open(`/bank-template-wizard?stmt_id=${stmtId}`, '_blank');
          }}
          onForceMatch={async () => {
            setShowExplain(null);
            await autoMatch(true);
          }}
        />
      )}
    </div>
  );
}


// ── Explain Health modal ────────────────────────────────────────────


function ExplainHealthModal({ stmt, gated, gateInfo, onClose, onOpenReparse, onOpenWizard, onForceMatch }) {
  const val = stmt.validation || {};
  const findings = val.findings || [];
  const conf = val.confidence ?? 0;
  const tone = conf >= 0.95 ? 'emerald' : conf >= 0.8 ? 'amber' : 'red';
  const headerCls = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    amber:   'bg-amber-50 border-amber-200 text-amber-900',
    red:     'bg-red-50 border-red-200 text-red-900',
  }[tone];
  const fixForCode = (code) => {
    if (['opening_closing_mismatch', 'balance_drift', 'no_lines',
         'missing_descriptions', 'zero_amount_rows'].includes(code)) {
      return { label: 'افتح Template Wizard', action: onOpenWizard };
    }
    if (code === 'possible_duplicates') {
      return { label: 'إعادَة التَحليل', action: onOpenReparse };
    }
    return { label: 'إعادَة التَحليل', action: onOpenReparse };
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-2" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white rounded-2xl w-full max-w-2xl max-h-[88vh] overflow-y-auto shadow-2xl"
           data-testid="explain-health-modal">
        <div className={`px-5 py-3 border-b ${headerCls} rounded-t-2xl`}>
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold flex items-center gap-2">
                {gated && '🚫'} اشرَح الجَودَة — {Math.round(conf * 100)}%
              </div>
              <div className="text-xs opacity-80 mt-0.5">
                {gated ? 'لا يُمكِن تَشغيل المُطابَقة التِلقائية لأَنّ الجَودَة أَقَل من حَدّ القَبول.' : 'كَيف وَصَلَ النِظام لِهذِه النِسبَة، وَكَيف تُصلِح كُل مُلاحَظَة.'}
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-md hover:bg-white/40">
              <XIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="p-4 space-y-3">
          {gated && gateInfo && (
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs">
              <div className="font-semibold text-amber-800">حَدّ القَبول: {Math.round(gateInfo.threshold * 100)}% · الجَودَة الحالية: {Math.round(gateInfo.confidence * 100)}%</div>
              <div className="mt-1 text-amber-900">{gateInfo.hint}</div>
            </div>
          )}
          {findings.length === 0 ? (
            <div className="p-6 text-center text-emerald-700 bg-emerald-50 border border-emerald-200 rounded">
              ✓ لا تُوجَد مُلاحَظات — الكَشف صَحي 100%.
            </div>
          ) : (
            <ul className="space-y-2">
              {findings.map((f, i) => {
                const sev = f.severity || 'info';
                const sevCls = sev === 'error' ? 'border-red-200 bg-red-50 text-red-800'
                  : sev === 'warning' ? 'border-amber-200 bg-amber-50 text-amber-800'
                  : 'border-slate-200 bg-slate-50 text-slate-700';
                const icon = sev === 'error' ? '✖' : sev === 'warning' ? '⚠' : 'ℹ';
                const fix = fixForCode(f.code);
                return (
                  <li key={i} className={`border rounded-lg p-3 ${sevCls}`} data-testid={`finding-${f.code}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="font-semibold text-sm">{icon} {f.message}</div>
                        <div className="text-[11px] mt-0.5 opacity-70 font-mono">{f.code}</div>
                      </div>
                      <button onClick={fix.action}
                              data-testid={`fix-${f.code}`}
                              className="rounded-lg bg-white hover:bg-slate-50 border border-slate-300 text-slate-800 text-xs font-semibold px-3 py-1.5 whitespace-nowrap">
                        {fix.label}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="px-5 py-3 border-t flex items-center justify-between gap-2 bg-slate-50">
          <div className="text-xs text-slate-500">
            القالِب الحالي: <span className="font-mono">{stmt.template_key || '—'}</span> v{stmt.template_version || 1}
          </div>
          <div className="flex items-center gap-2">
            {gated && (
              <button onClick={onForceMatch}
                      data-testid="force-match-btn"
                      className="px-3 py-1.5 text-xs rounded-lg bg-amber-600 hover:bg-amber-700 text-white">
                ⚠ تَشغيل بِالقُوَّة (Admin)
              </button>
            )}
            <button onClick={onOpenWizard}
                    data-testid="open-wizard-btn"
                    className="px-3 py-1.5 text-xs rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white">
                افتح Template Wizard
            </button>
            <button onClick={onClose} className="px-3 py-1.5 text-xs rounded-lg border border-slate-300 hover:bg-white">
              إغلاق
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ── Re-Parse modal ──────────────────────────────────────────────────


function ReparseModal({ stmt, templates, onClose, onDone }) {
  const [pick, setPick] = useState(stmt.template_key || '');
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await axiosInstance.post(
        `/bank-recon/${stmt.id}/re-parse`,
        null, { params: pick ? { template_key: pick } : {} },
      );
      toast.success(`تَمَّت إعادَة التَحليل — ${data.lines_count} بَند (تَم الإبقاء عَلى ${data.preserved})`);
      onDone();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : det?.hint || 'فَشَلَت إعادَة التَحليل');
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-2" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white rounded-2xl w-full max-w-md shadow-2xl"
           data-testid="reparse-modal">
        <div className="px-5 py-3 border-b">
          <div className="font-semibold">إعادَة تَحليل الكَشف</div>
          <div className="text-xs text-slate-500 mt-1">
            القالِب الحالي: <span className="font-mono">{stmt.template_key || '—'}</span> v{stmt.template_version || 1}
          </div>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600">اختَر قالِباً جَديداً</label>
            <select value={pick} onChange={(e) => setPick(e.target.value)}
                    data-testid="reparse-template-picker"
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
              <option value="">— نَفس القالِب الحالي —</option>
              {templates.filter(t => t.enabled !== false).map(t => (
                <option key={t.template_key} value={t.template_key}>
                  {t.name} ({t.template_key} v{t.version})
                </option>
              ))}
            </select>
          </div>
          <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            ⚠️ ستُحذف نَتائج المُطابَقة التِلقائية لِلبُنود غَير المُطابَقَة يَدوياً. البُنود المُطابَقَة يَدَوياً سَتَبقى مَحفوظَة.
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2 bg-slate-50">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 hover:bg-white">إلغاء</button>
          <button onClick={run} disabled={busy}
                  data-testid="reparse-run-btn"
                  className="px-3 py-1.5 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white">
            {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'إعادَة التَحليل'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ── Parse log modal ─────────────────────────────────────────────────


function ParseLogModal({ stmtId, onClose }) {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    axiosInstance.get(`/bank-recon/${stmtId}/parse-log`)
      .then(({ data }) => setInfo(data))
      .catch(() => setInfo({ parse_log: [], warnings: ['تَعَذَّر التَحميل'] }));
  }, [stmtId]);
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-2" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white rounded-2xl w-full max-w-3xl max-h-[85vh] overflow-y-auto shadow-2xl"
           data-testid="parse-log-modal">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <div>
            <div className="font-semibold">سِجِل التَحليل (Parse Log)</div>
            <div className="text-xs text-slate-500 mt-1">شَفافِيَّة كامِلَة لِكُل خُطوَة تَحليل.</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100">
            <XIcon className="w-4 h-4" />
          </button>
        </div>
        {!info ? (
          <div className="p-10 text-center text-slate-500">جارٍ التَحميل…</div>
        ) : (
          <div className="p-4 space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              <div className="bg-slate-50 rounded p-2"><div className="text-slate-500">القالِب</div><div className="font-mono">{info.template_key || '—'} v{info.template_version || 1}</div></div>
              <div className="bg-slate-50 rounded p-2"><div className="text-slate-500">دِقَّة الكَشف</div><div className="font-mono">{Math.round((info.detection_confidence || 0) * 100)}%</div></div>
              <div className="bg-slate-50 rounded p-2"><div className="text-slate-500">إصدار المُحَلِّل</div><div className="font-mono">{info.parser_version || '—'}</div></div>
              <div className="bg-slate-50 rounded p-2"><div className="text-slate-500">إعادَة التَحليل</div><div className="font-mono">{info.re_parse_count || 0}×</div></div>
            </div>
            {info.checksum_sha256 && (
              <div className="text-[11px] text-slate-500">
                SHA-256: <span className="font-mono break-all">{info.checksum_sha256}</span>
              </div>
            )}
            {(info.warnings || []).length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs">
                <div className="font-semibold text-amber-800 mb-1">تَحذيرات ({info.warnings.length})</div>
                <ul className="list-disc list-inside text-amber-900 space-y-0.5">
                  {info.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}
            {info.validation?.findings?.length > 0 && (
              <div className="border rounded p-3 text-xs space-y-2" data-testid="validation-findings">
                <div className="font-semibold flex items-center justify-between">
                  <span>نَتائج فاحِص الجَودَة</span>
                  <span className="font-mono text-slate-500">جودَة: {Math.round((info.validation.confidence || 0) * 100)}%</span>
                </div>
                <ul className="space-y-1">
                  {info.validation.findings.map((f, i) => {
                    const tone = f.severity === 'error' ? 'bg-red-50 border-red-200 text-red-800'
                      : f.severity === 'warning' ? 'bg-amber-50 border-amber-200 text-amber-800'
                      : 'bg-slate-50 border-slate-200 text-slate-700';
                    return (
                      <li key={i} className={`border rounded p-2 ${tone}`}>
                        <div className="font-semibold">[{f.severity}] {f.code}</div>
                        <div>{f.message}</div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            <div>
              <div className="text-xs font-semibold text-slate-700 mb-1">السِجِل التَفصيلي</div>
              <pre className="bg-slate-900 text-emerald-300 text-[11px] font-mono p-3 rounded overflow-x-auto whitespace-pre-wrap leading-relaxed">
                {(info.parse_log || []).map((l, i) => `${String(i+1).padStart(3,'0')}  ${l}`).join('\n') || '(فارِغ)'}
              </pre>
            </div>
            {info.detection_scores && (
              <div>
                <div className="text-xs font-semibold text-slate-700 mb-1">نَتائج اكتِشاف القَوالِب</div>
                <table className="text-xs w-full">
                  <tbody>
                    {Object.entries(info.detection_scores).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                      <tr key={k} className="border-b last:border-b-0">
                        <td className="py-1 font-mono">{k}</td>
                        <td className="py-1 text-end">
                          <span className="inline-block w-24 bg-slate-200 rounded-full overflow-hidden">
                            <span className="block h-2 bg-indigo-500" style={{ width: `${Math.min(100, v * 100)}%` }} />
                          </span>
                          <span className="ms-2 font-mono">{Math.round(v * 100)}%</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// ── Manual match drawer ─────────────────────────────────────────────


function ManualMatchDrawer({ line, onClose, onMatched }) {
  const [busy, setBusy] = useState(false);

  const suggestions = line.suggestions || [];

  const apply = async (cand) => {
    setBusy(true);
    try {
      await axiosInstance.post(`/bank-recon/lines/${line.id}/manual-match`, null, {
        params: { candidate_id: cand.candidate_id, candidate_kind: cand.candidate_kind },
      });
      toast.success('تَمَّت المُطابَقة اليَدوية');
      onMatched();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فَشَلَت المُطابَقة');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-2" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto shadow-2xl"
        data-testid="manual-match-drawer"
      >
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold">مُطابَقة يَدَوية</div>
            <div className="text-xs text-slate-500 mt-0.5">
              {line.date} · {line.description} · <span className="font-mono">{fmt(line.amount)}</span>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100">
            <XIcon className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-2">
          {suggestions.length === 0 ? (
            <div className="text-center text-slate-500 py-8">
              <AlertTriangle className="w-8 h-8 mx-auto opacity-40 mb-2" />
              لا توجَد اقتِراحات. شَغِّل «المُطابَقة التِلقائية» أوَّلاً.
            </div>
          ) : suggestions.map((s) => (
            <div key={s.candidate_id} className="border rounded-lg p-3 flex items-center justify-between gap-3 hover:bg-slate-50">
              <div>
                <div className="text-xs font-mono text-slate-600">{s.candidate_id}</div>
                <div className="text-[11px] text-slate-500 mt-1">
                  النَوع: <span className="font-semibold">{s.candidate_kind || '—'}</span> ·
                  الثِقة: <span className="font-semibold">{Math.round((s.confidence || 0) * 100)}%</span>
                </div>
                <div className="text-[10px] text-slate-400 mt-1 flex flex-wrap gap-x-2">
                  {(s.scores || []).filter(x => x.score > 0).map((x, i) => (
                    <span key={i}>{x.rule}: {Math.round(x.score * 100)}%</span>
                  ))}
                </div>
              </div>
              <button
                onClick={() => apply(s)}
                disabled={busy}
                data-testid={`apply-candidate-${s.candidate_id}`}
                className="rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-semibold px-3 py-2"
              >
                اختيار هذا
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ── Page shell ──────────────────────────────────────────────────────


export default function BankReconciliation() {
  const [accounts, setAccounts] = useState([]);
  const [bankAccountId, setBankAccountId] = useState('');
  const [statements, setStatements] = useState([]);
  const [pickedId, setPickedId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [healthFilter, setHealthFilter] = useState('all');
  const [healthCounts, setHealthCounts] = useState({ green: 0, amber: 0, red: 0 });

  const loadAccounts = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/bank/accounts');
      const items = Array.isArray(data) ? data : (data.items || []);
      setAccounts(items);
      setBankAccountId((prev) => (prev || (items.length === 1 ? items[0].id : '')));
    } catch (e) {
      toast.error('تَعَذَّر تَحميل الحِسابات البَنكية');
    }
  }, []);

  const loadStatements = useCallback(async () => {
    try {
      const params = {};
      if (healthFilter && healthFilter !== 'all') params.health_band = healthFilter;
      const { data } = await axiosInstance.get('/bank-recon/statements', { params });
      const items = (data.items || []).filter((s) =>
        !bankAccountId ? true : s.bank_account_id === bankAccountId,
      );
      setStatements(items);
      setHealthCounts(data.counts || { green: 0, amber: 0, red: 0 });
    } catch {
      setStatements([]);
    }
  }, [bankAccountId, healthFilter]);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);
  useEffect(() => { loadStatements(); }, [loadStatements]);

  const onUpload = async (file) => {
    if (!bankAccountId) { toast.error('اختَر الحساب البَنكي أوَّلاً'); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await axiosInstance.post('/bank-recon/import', form, {
        params: { bank_account_id: bankAccountId },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const tplMsg = data.template_used
        ? ` · القالِب: ${data.template_used} (${Math.round((data.detection_confidence || 0) * 100)}%)`
        : '';
      const valMsg = data.validation
        ? ` · جودَة: ${Math.round((data.validation.confidence || 0) * 100)}%`
        : '';
      toast.success(`تَم استيراد ${data.lines_count} بَند${tplMsg}${valMsg}`);
      await loadStatements();
      setPickedId(data.id);
    } catch (e) {
      const det = e?.response?.data?.detail;
      const msg = typeof det === 'string' ? det : det?.hint || 'فَشَل الاستيراد';
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-4 max-w-7xl mx-auto" dir="rtl">
      {pickedId ? (
        <StatementDetail
          stmtId={pickedId}
          onBack={() => { setPickedId(null); loadStatements(); }}
          accounts={accounts}
        />
      ) : (
        <StatementsList
          accounts={accounts}
          statements={statements}
          onPick={setPickedId}
          onUpload={onUpload}
          uploading={uploading}
          bankAccountId={bankAccountId}
          setBankAccountId={setBankAccountId}
          healthFilter={healthFilter}
          setHealthFilter={setHealthFilter}
          healthCounts={healthCounts}
        />
      )}
    </div>
  );
}
